/**
 * Finding a person, and finding an account.
 *
 * ---------------------------------------------------------------------
 * WHY THIS EXISTS
 *
 * Several screens in this product asked for a uuid in a text box. The
 * tee sheet's booking form wanted a "membership id", a "person id" and a
 * "sponsor person id"; the pool deck wanted "the member's person id";
 * the golf professional's record and the committee roster the same.
 *
 * Nothing anywhere in this product ever shows anybody a uuid. So those
 * fields could not be filled in — not by staff, not by a manager, not by
 * whoever wrote them. The tee sheet, the screen a golf professional
 * lives in, could not book a tee time.
 *
 * The screens that DID search each wrote their own, and each got a
 * different subset right: one searched accounts where it wanted people,
 * one matched `first || ' ' || last` and so could not find anybody by
 * the name their club actually calls them.
 *
 * One pair of components, over the two endpoints that exist:
 *
 *   · `<PersonLookup>`  → `/v1/people/search`     — a person: a player,
 *     a swimmer, a committee member, a professional.
 *   · `<AccountLookup>` → `/v1/member-accounts`   — a household: whose
 *     account a charge lands on.
 *
 * Which one a screen wants is not a matter of taste. A green fee is
 * charged to an ACCOUNT and played by a PERSON, and a screen that
 * confuses them makes the desk pick a household and then guess which
 * member of it is standing there.
 *
 * ---------------------------------------------------------------------
 * WHAT IT DOES THAT A TEXT BOX DID NOT
 *
 *   · Debounces. The old inline copies fired a request per keystroke;
 *     "Whitfield" is ten searches of which nine are thrown away.
 *   · Arrow keys and Enter. Somebody at a counter is typing, and making
 *     them reach for a mouse to accept the only result is a second a
 *     hundred times a day.
 *   · Says which person, not just which name. Three Whitfields come back
 *     for "Whitfield"; the row carries the role and the account, which is
 *     what tells them apart.
 *   · Says when there is nothing, and why. A silent empty list reads as
 *     a broken search, which is the complaint this replaces.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useApi } from '../lib/useApi.ts';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

/** One row of `/v1/people/search` — see `fn_person_search`, 0190. */
export interface PersonHit {
  person_id: string;
  label: string;
  first_name: string;
  last_name: string;
  preferred_name: string | null;
  email: string | null;
  phone: string | null;
  age: number | null;
  is_minor: boolean;
  is_active: boolean;
  membership_id: string | null;
  membership_number: string | null;
  membership_status: string | null;
  membership_role: string | null;
  may_charge: boolean | null;
  member_account_id: string | null;
  account_number: string | null;
  account_name: string | null;
  account_status: string | null;
}

export interface AccountHit {
  member_account_id: string;
  account_number: string;
  display_name: string;
  status: string;
  membership_id?: string | null;
  membership_number: string | null;
  category_name?: string | null;
  balance_minor?: number | string;
}

/**
 * A search box that only asks the server once the typing stops.
 *
 * 220ms: long enough that a name typed at speed is one request, short
 * enough that it does not feel like waiting. Below about 150ms it is
 * per-keystroke again; above about 300ms the results feel late.
 */
function useDebounced(value: string, ms = 220): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

/**
 * The list of results, and the keyboard that drives it.
 *
 * Shared by both lookups because the behaviour is identical and the only
 * difference is what a row says.
 */
function Results<T>({
  items,
  loading,
  searched,
  keyOf,
  render,
  onPick,
  emptyHint,
}: {
  items: T[];
  loading: boolean;
  searched: boolean;
  keyOf: (item: T) => string;
  render: (item: T) => ReactNode;
  onPick: (item: T) => void;
  emptyHint: string;
}) {
  const [cursor, setCursor] = useState(0);

  // A new set of results starts at the top. Leaving the cursor where it
  // was means Enter picks the fourth result of a search nobody has read.
  useEffect(() => setCursor(0), [items]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === 'Enter') {
        const pick = items[cursor];
        if (pick) {
          e.preventDefault();
          onPick(pick);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, cursor, onPick]);

  if (loading) return <div className="lookup__note">Searching…</div>;
  if (!searched) return null;
  if (items.length === 0) return <div className="lookup__note">{emptyHint}</div>;

  return (
    <div className="lookup__results" role="listbox">
      {items.map((item, i) => (
        <button
          key={keyOf(item)}
          type="button"
          role="option"
          aria-selected={i === cursor}
          className={i === cursor ? 'lookup__hit lookup__hit--on' : 'lookup__hit'}
          onMouseEnter={() => setCursor(i)}
          onClick={() => onPick(item)}
        >
          {render(item)}
        </button>
      ))}
    </div>
  );
}

/** What is chosen, with the way to un-choose it. */
function Chosen({ children, onClear }: { children: ReactNode; onClear: () => void }) {
  return (
    <div className="lookup__chosen">
      <div className="lookup__chosen-body">{children}</div>
      <button type="button" className="secondary small" onClick={onClear}>
        Change
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A person                                                            */
/* ------------------------------------------------------------------ */

export function PersonLookup({
  label,
  hint,
  value,
  onChange,
  placeholder = 'Name, membership number, email or phone',
  includeInactive = false,
  filter,
  autoFocus,
}: {
  label: string;
  hint?: ReactNode;
  value: PersonHit | null;
  onChange: (person: PersonHit | null) => void;
  placeholder?: string;
  /** Former members and ended staff. Off by default. */
  includeInactive?: boolean;
  /** Narrow the results — e.g. only people who may charge. */
  filter?: (p: PersonHit) => boolean;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState('');
  const search = useDebounced(text.trim());
  const box = useRef<HTMLInputElement>(null);

  const hits = useApi<{ people: PersonHit[] }>(
    search.length >= 2
      ? `/v1/people/search?limit=12&search=${encodeURIComponent(search)}` +
          (includeInactive ? '&include_inactive=true' : '')
      : null,
    [search, includeInactive],
  );

  const people = (hits.data?.people ?? []).filter((p) => (filter ? filter(p) : true));

  // Not a <label> wrapping the results: a label forwards a click on
  // anything inside it to its own control, which swallows the click on a
  // result and leaves the form looking broken. Found the hard way on the
  // court sheet.
  return (
    <div className="field-block">
      <span className="field-label">{label}</span>
      {hint && <div className="hint" style={{ marginBottom: 5 }}>{hint}</div>}

      {value ? (
        <Chosen onClear={() => { onChange(null); setText(''); box.current?.focus(); }}>
          <strong>{value.label}</strong>
          <span className="muted small">
            {[
              value.membership_role,
              value.membership_number ?? value.account_number,
              value.account_name,
            ]
              .filter(Boolean)
              .join(' · ') || 'no membership'}
          </span>
        </Chosen>
      ) : (
        <>
          <input
            ref={box}
            value={text}
            autoFocus={autoFocus}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            // Off, all of it. A browser offering last week's search terms
            // over a live list of members is the wrong list on top of the
            // right one.
            autoComplete="off"
            spellCheck={false}
          />
          {text.trim().length === 1 && (
            <div className="lookup__note">One more letter.</div>
          )}
          <Results
            items={people}
            loading={hits.loading}
            searched={search.length >= 2}
            keyOf={(p) => `${p.person_id}:${p.membership_id ?? 'none'}`}
            onPick={(p) => { onChange(p); setText(''); }}
            emptyHint={`Nobody matches “${search}”. The search covers names either way round, the name the club uses, membership and account numbers, email and telephone.`}
            render={(p) => (
              <>
                <span className="lookup__name">
                  {p.label}
                  {!p.is_active && <span className="muted small"> · no longer active</span>}
                </span>
                <span className="lookup__aside">
                  {[
                    p.membership_role,
                    p.membership_number,
                    p.account_name,
                    // The distinguishing detail, when the name is not
                    // one. Three Whitfields is the common case and the
                    // whole reason a row says more than a name.
                    p.email,
                  ]
                    .filter(Boolean)
                    .join(' · ') || 'not on a membership'}
                </span>
              </>
            )}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* An account                                                          */
/* ------------------------------------------------------------------ */

export function AccountLookup({
  label,
  hint,
  value,
  onChange,
  placeholder = 'Name, account or membership number, email or phone',
  autoFocus,
}: {
  label: string;
  hint?: ReactNode;
  value: AccountHit | null;
  onChange: (account: AccountHit | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState('');
  const search = useDebounced(text.trim());
  const box = useRef<HTMLInputElement>(null);

  const hits = useApi<{ accounts: AccountHit[] }>(
    search.length >= 2
      ? `/v1/member-accounts?limit=12&search=${encodeURIComponent(search)}`
      : null,
    [search],
  );

  const accounts = hits.data?.accounts ?? [];

  return (
    <div className="field-block">
      <span className="field-label">{label}</span>
      {hint && <div className="hint" style={{ marginBottom: 5 }}>{hint}</div>}

      {value ? (
        <Chosen onClear={() => { onChange(null); setText(''); box.current?.focus(); }}>
          <strong>{value.display_name}</strong>
          <span className="muted small">
            {value.membership_number ?? value.account_number}
          </span>
        </Chosen>
      ) : (
        <>
          <input
            ref={box}
            value={text}
            autoFocus={autoFocus}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
          />
          {text.trim().length === 1 && <div className="lookup__note">One more letter.</div>}
          <Results
            items={accounts}
            loading={hits.loading}
            searched={search.length >= 2}
            keyOf={(a) => a.member_account_id}
            onPick={(a) => { onChange(a); setText(''); }}
            emptyHint={`No account matches “${search}”. The search covers the account name and number, the membership number, and every person on the membership — a spouse or a dependent finds the household.`}
            render={(a) => (
              <>
                <span className="lookup__name">{a.display_name}</span>
                <span className="lookup__aside">
                  {[a.membership_number ?? a.account_number, a.category_name]
                    .filter(Boolean)
                    .join(' · ')}
                  {a.status !== 'current' && (
                    <span className="lookup__flag"> {a.status.replace('_', ' ')}</span>
                  )}
                </span>
              </>
            )}
          />
        </>
      )}
    </div>
  );
}
